import { type AdfSmartLink } from '@confluence/connectors/entry-points/extractSmartLinksFromAdf.ts';

import { matchProviderKey } from '../providerPatterns.ts';
import { type QualifyingProduct } from '../thirdPartyPromptTypes.ts';

import { resolveProviderKeyToAppId } from './resolveProviderKeyToAppId.ts';

/** How often the page's Atlassian Document Format references each product. */
const tallyByAppId = (links: readonly AdfSmartLink[]): Map<string, number> => {
	const totals = new Map<string, number>();

	for (const { url, count } of links) {
		const providerKey = matchProviderKey(url);
		const appId = providerKey ? resolveProviderKeyToAppId(providerKey) : undefined;

		if (appId) {
			// A card node and a link mark for one product add together, so two spellings still sum.
			totals.set(appId, (totals.get(appId) ?? 0) + count);
		}
	}

	return totals;
};

/**
 * The candidate the page references most. `undefined` when the walk matches none of them, which the
 * caller reads as "restore the first-party card".
 */
export const chooseMostReferencedProduct = (
	candidates: readonly QualifyingProduct[],
	links: readonly AdfSmartLink[],
): QualifyingProduct | undefined => {
	const totals = tallyByAppId(links);

	let winner: QualifyingProduct | undefined;
	let best = 0;

	for (const candidate of candidates) {
		const total = totals.get(candidate.appId) ?? 0;

		// Strictly greater, so a tie keeps the order detection returned rather than picking at random.
		if (total > best) {
			winner = candidate;
			best = total;
		}
	}

	return winner;
};
